/**
 * Sector knowledge — per faction, never shared (SECTOR_RFC Q4; the fog rule
 * one rung up from the match).
 *
 * The truth lives in `SectorState`. Each faction keeps a `FactionKnowledge`
 * of what it has seen and when; `knownView(state, faction)` rebuilds a
 * `SectorState`-shaped view from it — own squads as they are, enemy squads
 * as last seen, system owners as last seen — so the commander and the sheet
 * plan on the view with no code of their own for fog.
 *
 * Sources (first pass): a faction sees every system where it has a squad or
 * that it holds, and the lanes out of those systems (squads in transit on
 * them); both sides of a battle see its system and each other's survivors;
 * a scout order reveals an adjacent system after SCOUT_DAYS. Everything
 * else ages: a report carries `seenDay`, and the view keeps it. Sensors and
 * comms are later sources that feed the same record.
 */

import { memorySummary } from './garrison';
import type { FactionId, FactionKnowledge, KnownSquad, KnownSystem, SectorState, Squad, SquadId, SystemId } from './types';
import { dpow } from '../core/dmath';
import { tracesAt } from './signatures';
import { relayReports, type ReportFrom } from './comms';
import { passOver } from './satellites';
import { closeEyes, decoyReport, exposeDecoys, liveDecoys } from './decoys';

export const SCOUT_DAYS = 1;

/** What every faction knows on day 0: the map, every faction's home, its own squads. */
export function initialKnowledge(state: SectorState, faction: FactionId): FactionKnowledge {
  const systems: Record<SystemId, KnownSystem> = {};
  for (const f of state.factions) systems[f.home] = { systemId: f.home, owner: f.id, garrisoned: true, seenDay: 0 };
  return { faction, systems, squads: {} };
}

/**
 * X29: steal their picture. When `taker` takes `systemId` from `from`, what `from` knew comes with the ground — every
 * system and squad report of theirs that `taker` lacks or holds older — marked theirs and dated. The lanes they
 * faced come as the garrison's memory did (X17, logged at the capture). Nothing fresher of the taker's is replaced.
 */
export function stealPicture(state: SectorState, taker: FactionId, from: FactionId, systemId: SystemId): { state: SectorState; systems: number; squads: number } {
  const theirs = state.knowledge[from];
  if (!theirs || taker === from) return { state, systems: 0, squads: 0 };
  const mine = state.knowledge[taker] ?? initialKnowledge(state, taker);
  const systems = { ...mine.systems };
  const squads = { ...mine.squads };
  let ns = 0, nq = 0;
  for (const [id, r] of Object.entries(theirs.systems)) {
    const have = systems[id];
    if (have && have.seenDay >= r.seenDay) continue;
    systems[id] = { ...r, via: { faction: from, day: state.day } }; ns++;
  }
  for (const [id, r] of Object.entries(theirs.squads)) {
    if (r.faction === taker) continue;   // their reports of us tell us nothing
    const have = squads[id];
    if (have && have.seenDay >= r.seenDay) continue;
    squads[id] = { ...r, via: { faction: from, day: state.day } }; nq++;
  }
  const sys = state.systems.find(x => x.id === systemId);
  const next: SectorState = { ...state, knowledge: { ...state.knowledge, [taker]: { ...mine, systems, squads } } };
  return { state: ns || nq ? { ...next, log: [...next.log, { day: state.day, kind: 'scout', text: `${taker} takes ${from}'s picture with ${sys?.name ?? systemId}: ${ns} system report${ns === 1 ? '' : 's'}, ${nq} squad report${nq === 1 ? '' : 's'}, as they stood`, systemId, faction: taker }] } : next, systems: ns, squads: nq };
}

/** The systems a faction currently watches: where it has a squad, and what it holds. */
export function watchedSystems(state: SectorState, faction: FactionId): Set<SystemId> {
  const watched = new Set<SystemId>();
  for (const sys of state.systems) if (sys.owner === faction) watched.add(sys.id);
  for (const q of state.squads) if (q.faction === faction && q.at !== null && q.units.some(u => u.alive)) watched.add(q.at);
  // an antenna on a held system watches the systems one lane out as well (56.5); a sensor (56.8) watches two lanes out
  const hop = (from: Set<SystemId>): Set<SystemId> => {
    const out = new Set<SystemId>();
    for (const l of state.lanes) { if (from.has(l.a)) out.add(l.b); if (from.has(l.b)) out.add(l.a); }
    return out;
  };
  for (const sys of state.systems) {
    if (sys.owner !== faction) continue;
    const antenna = sys.structures.some(b => b.kind === 'antenna');
    const sensor = sys.structures.some(b => b.kind === 'sensor');
    if (!antenna && !sensor) continue;
    const one = hop(new Set([sys.id]));
    for (const id of one) watched.add(id);
    if (sensor) for (const id of hop(one)) watched.add(id);
  }
  return watched;
}

/** Lanes from `systemId` to the nearest system `faction` watches (0 if it watches the system itself; Infinity if none reachable). */
export function lanesToWatched(state: SectorState, faction: FactionId, systemId: SystemId): number {
  const watched = watchedSystems(state, faction);
  if (watched.has(systemId)) return 0;
  // breadth-first over lanes
  const dist = new Map<SystemId, number>([[systemId, 0]]);
  const queue: SystemId[] = [systemId];
  while (queue.length) {
    const cur = queue.shift()!;
    const d = dist.get(cur)!;
    for (const l of state.lanes) {
      const next = l.a === cur ? l.b : l.b === cur ? l.a : null;
      if (next === null || dist.has(next)) continue;
      if (watched.has(next)) return d + 1;
      dist.set(next, d + 1);
      queue.push(next);
    }
  }
  return Infinity;
}

/**
 * 56.8 comms rule: who hears a loud battle at `systemId` — its sides, and every faction with a watched system
 * within `rules.commsLanes` lanes of it (Infinity: everyone, as before).
 */
export function factionsHearing(state: SectorState, systemId: SystemId, sides: readonly FactionId[]): FactionId[] {
  const range = state.rules.commsLanes;
  return state.factions.map(f => f.id).filter(fid => sides.includes(fid) || lanesToWatched(state, fid, systemId) <= range);
}

/** A faction learns where one squad stands now (56.8: a scout seen by a sensor or antenna at the system it looked at). */
export function observeSquad(state: SectorState, faction: FactionId, squadId: SquadId): SectorState {
  const q = state.squads.find(x => x.id === squadId);
  if (!q) return state;
  const k = state.knowledge[faction] ?? initialKnowledge(state, faction);
  return relayReports(state, { ...state, knowledge: { ...state.knowledge, [faction]: { ...k, squads: { ...k.squads, [squadId]: snapshot(q, state.day) } } } });
}

/**
 * 56.8 stale reports: what a faction should count an enemy squad as, by how old its report is —
 * strength × 0.5^(age / rules.reportHalfLifeDays); a half-life of 0 means reports never age (today).
 */
export function discountedStrength(state: SectorState, faction: FactionId, squadId: SquadId, strength: number): number {
  const half = state.rules.reportHalfLifeDays;
  if (!(half > 0)) return strength;
  const age = squadReportAge(state, faction, squadId);
  if (!isFinite(age)) return strength;
  return strength * dpow(0.5, age / half);
}

/**
 * Record what each faction can see right now. Called by the clock after every
 * step and by applyBattleOutcome; idempotent for the same day.
 */
export function observe(state: SectorState): SectorState {
  const knowledge: Record<FactionId, FactionKnowledge> = { ...state.knowledge };
  for (const f of state.factions) {
    const k = knowledge[f.id] ?? initialKnowledge(state, f.id);
    const watched = watchedSystems(state, f.id);
    if (state.rules.satellites) watched.add(passOver(state, f.id, state.day));   // X4: today's pass is a look
    const systems = { ...k.systems };
    const squads = { ...k.squads };
    for (const id of watched) {
      const sys = state.systems.find(x => x.id === id)!;
      systems[id] = { systemId: id, owner: sys.owner, garrisoned: sys.garrisoned, seenDay: state.day, ...knownTraces(state, id) };
    }
    for (const q of state.squads) {
      if (q.faction === f.id) continue;
      // a sneaking squad travels dark: unseen on lanes, seen only where someone stands
      const seen = q.at !== null
        ? watched.has(q.at)
        : q.transit !== null && q.stance !== 'sneak' && (watched.has(q.transit.from) || watched.has(q.transit.to));
      if (!seen) continue;
      squads[q.id] = snapshot(q, state.day);
    }
    // X3: a decoy reads as a squad to eyes that are not there (an antenna's or sensor's watch, a satellite pass);
    // eyes in the system itself expose it (below)
    if (state.rules.decoys) for (const d of liveDecoys(state)) {
      if (d.faction === f.id || d.exposedTo.includes(f.id) || !watched.has(d.at) || closeEyes(state, f.id, d.at)) continue;
      squads[`decoy-${d.id}`] = decoyReport(d, state.day);
    }
    knowledge[f.id] = { ...k, faction: f.id, systems, squads };
  }
  // X21: a look reports from where its eyes stand — a squad or a holder on the system; an antenna, a sensor or the
  // satellite's downlink are on the net already, so their looks arrive at once
  const from: ReportFrom = (fid, about) => {
    if (state.squads.some(q => q.faction === fid && q.at === about && q.units.some(u => u.alive))) return about;
    if (state.systems.find(x => x.id === about)?.owner === fid) return about;
    return null;
  };
  let next = relayReports(state, { ...state, knowledge }, from);
  // X3: eyes in the system itself see a decoy for what it is
  if (state.rules.decoys) for (const d of liveDecoys(next)) for (const f of next.factions) if (f.id !== d.faction && closeEyes(next, f.id, d.at)) next = exposeDecoys(next, f.id, d.at);
  return next;
}

/** Both sides of a battle learn its system and each other's survivors (they were standing there). */
export function observeBattle(state: SectorState, systemId: SystemId, factions: FactionId[]): SectorState {
  const knowledge: Record<FactionId, FactionKnowledge> = { ...state.knowledge };
  const sys = state.systems.find(x => x.id === systemId)!;
  for (const fid of factions) {
    const k = knowledge[fid] ?? initialKnowledge(state, fid);
    const squads = { ...k.squads };
    for (const q of state.squads) if (q.faction !== fid && q.at === systemId) squads[q.id] = snapshot(q, state.day);
    knowledge[fid] = { ...k, systems: { ...k.systems, [systemId]: { systemId, owner: sys.owner, garrisoned: sys.garrisoned, seenDay: state.day, ...knownTraces(state, systemId) } }, squads };
  }
  return relayReports(state, { ...state, knowledge });
}

/** A scout report: the adjacent system's owner and the squads standing there. */
export function observeSystem(state: SectorState, faction: FactionId, systemId: SystemId, eyesAt?: SystemId): SectorState {
  const k = state.knowledge[faction] ?? initialKnowledge(state, faction);
  const sys = state.systems.find(x => x.id === systemId)!;
  const squads = { ...k.squads };
  for (const q of state.squads) if (q.faction !== faction && q.at === systemId) squads[q.id] = snapshot(q, state.day);
  const looked = relayReports(state, {
    ...state,
    knowledge: { ...state.knowledge, [faction]: { ...k, systems: { ...k.systems, [systemId]: { systemId, owner: sys.owner, garrisoned: sys.garrisoned, seenDay: state.day, ...knownTraces(state, systemId), ...(state.rules.adaptiveGarrisons && sys.garrisonMemory ? { adapted: memorySummary(sys.garrisonMemory, state) } : {}) } }, squads } },
  }, eyesAt !== undefined ? () => eyesAt : undefined);
  return looked.rules.decoys ? exposeDecoys(looked, faction, systemId) : looked;   // X3: a scout's look is close
}

/** X1: the traces a report carries (the rule on, and any to carry). */
function knownTraces(state: SectorState, systemId: SystemId): { traces?: KnownSystem['traces'] } {
  if (!state.rules.signatures) return {};
  const traces = tracesAt(state, systemId);
  return traces.length ? { traces } : {};
}

function snapshot(q: Squad, day: number): KnownSquad {
  return {
    squadId: q.id, faction: q.faction, name: q.name,
    at: q.at, transitTo: q.transit?.to ?? null,
    strength: q.units.filter(u => u.alive).length,
    units: q.units.map(u => ({ ...u, kit: { ...u.kit, launcher: { ...u.kit.launcher }, suit: { ...u.kit.suit }, equipment: [...u.kit.equipment], traits: { ...u.kit.traits } } })),
    doctrine: { ...q.doctrine },
    seenDay: day,
  };
}

/**
 * The sector as `faction` knows it: own squads as they are; enemy squads as
 * last seen, standing where they were seen (a squad seen in transit is placed
 * at its destination); system owners as last seen, unknown systems neutral;
 * no pending battle details beyond its own. Squads of other factions that
 * were never seen are absent. Pure; call it fresh each time.
 */
export function knownView(state: SectorState, faction: FactionId): SectorState {
  const k = state.knowledge[faction] ?? initialKnowledge(state, faction);
  const systems = state.systems.map(sys => {
    const known = k.systems[sys.id];
    return { ...sys, owner: known ? known.owner : null, garrisoned: known ? known.garrisoned : false, planets: undefined };
  });
  const own = state.squads.filter(q => q.faction === faction);
  const ownIds = new Set(own.map(q => q.id));
  const seen: Squad[] = [];
  for (const ks of Object.values(k.squads)) {
    if (ownIds.has(ks.squadId)) continue;
    // a squad we have since confirmed dead (fought and wiped) is gone from the truth; drop it from the view too
    if (ks.strength === 0) continue;
    seen.push({
      id: ks.squadId, faction: ks.faction, name: ks.name,
      units: ks.units, doctrine: ks.doctrine, supply: 0, drones: 0,   // what a report does not tell: supply and drones
      at: ks.at ?? ks.transitTo, transit: null, order: { kind: 'hold' }, stance: 'assault',
    });
  }
  return { ...state, systems, squads: [...own, ...seen], pending: null };
}

/**
 * Did the holder of `systemId` know `squadId` was coming? Yes if it saw the
 * squad since the squad set out on its last hop (`departedDay`) — it watched it
 * leave, or saw it on the lane. What a recent battle leaves behind is the
 * system's alert floor (sector.ts `alertFloor`), not awareness. Evaluated on
 * arrival, before the arrival itself is observed.
 */
export function defenderAwareOf(state: SectorState, defender: FactionId, squadId: SquadId, systemId: SystemId, departedDay: number): boolean {
  const k = state.knowledge[defender];
  // X21: the garrison knows what it saw at its own door even while its report to the commander is in flight
  const ks = k?.squads[squadId] ?? k?.inbox?.find(r => r.squad?.squadId === squadId)?.squad;
  return !!ks && ks.seenDay >= departedDay - 1e-9;
}

/** Days since a faction last saw a squad (Infinity if never). */
export function squadReportAge(state: SectorState, faction: FactionId, squadId: SquadId): number {
  const ks = state.knowledge[faction]?.squads[squadId];
  return ks ? state.day - ks.seenDay : Infinity;
}
